import type { SoulMapDocumentStatus } from '@/services/practice-service';
import type { Message } from '@/store/useChatStore';

/** Header pill states in the LiFT chat page design. */
export type SoulMapHeaderState =
  | 'ready'
  | 'preparing'
  | 'hidden'
  | 'failed'
  | 'needs-review';

/**
 * Document phases in the LiFT chat page design, in the order a job moves.
 * `offered` precedes the job: the agent has offered the document and the card
 * carries the "Create my Soul Map document" action (LE-296, F1 option B).
 */
export type SoulMapDocumentPhase =
  | 'offered'
  | 'recomputing'
  | 'queued'
  | 'writing'
  | 'rendering'
  | 'ready'
  | 'failed'
  | 'needs-review';

export interface FullSoulMapDocument {
  url: string;
  downloadCount: number;
  sizeBytes: number | null;
  pageCount: number | null;
}

/** Job statuses shown as a phase. `ready` also needs a downloadable PDF. */
const JOB_PHASES: Partial<
  Record<SoulMapDocumentStatus['status'], SoulMapDocumentPhase>
> = {
  offered: 'offered',
  recomputing_profile: 'recomputing',
  queued: 'queued',
  generating: 'writing',
  rendering: 'rendering',
  failed: 'failed',
  expired: 'needs-review',
};

export interface SoulMapDocumentPhaseOptions {
  /** A refresh is on its way: show ready or expired as preparing. */
  treatExpiredAsPreparing?: boolean;
  /**
   * Offer an expired document again instead of asking for a birth-details
   * review, for students who never see that form (LE-382).
   */
  regenerateExpired?: boolean;
}

export function hasBirthProfileRecomputeError(
  status: SoulMapDocumentStatus | null | undefined
): boolean {
  return Boolean(status?.birth_profile_recompute_error);
}

/** Ordinary generation failure — never use retry after a birth-profile error. */
export function canRetrySoulMapDocument(
  status: SoulMapDocumentStatus | null | undefined
): boolean {
  return (
    status?.status === 'failed' &&
    status.can_retry === true &&
    !hasBirthProfileRecomputeError(status)
  );
}

/** The backend has staged work: a profile recompute or a document job. */
export function isSoulMapDocumentInProgress(
  status: SoulMapDocumentStatus['status'] | null | undefined
): boolean {
  return (
    status === 'recomputing_profile' ||
    status === 'queued' ||
    status === 'generating' ||
    status === 'rendering'
  );
}

/**
 * Older backends report `failed` between Celery retries (backoff 5s, 10s,
 * 20s, capped at 60s), so keep polling a failed report this long to pick up
 * a retry that queues it again.
 */
export const FAILED_GRACE_POLL_MS = 90_000;

/** When this report was first seen failed, so refetches never restart the grace window. */
export interface SoulMapFailedSince {
  reportKey: string;
  since: number;
}

export function trackSoulMapFailedSince(
  previous: SoulMapFailedSince | null,
  status: SoulMapDocumentStatus | null | undefined,
  now: number
): SoulMapFailedSince | null {
  if (status?.status !== 'failed') {
    return null;
  }
  const reportKey = String(status.report_id ?? '');
  if (previous?.reportKey === reportKey) {
    return previous;
  }
  return { reportKey, since: now };
}

export function isWithinSoulMapFailedGrace(
  failedSince: SoulMapFailedSince | null,
  now: number
): boolean {
  return failedSince !== null && now - failedSince.since < FAILED_GRACE_POLL_MS;
}

export function getSoulMapPreparingLabel(
  status: SoulMapDocumentStatus | null | undefined
): string {
  switch (status?.status) {
    case 'recomputing_profile':
      return 'Updating birth profile…';
    case 'queued':
      return 'Report queued…';
    case 'generating':
      return 'Creating your Soul Map…';
    case 'rendering':
      return 'Preparing PDF…';
    default:
      return 'Preparing Soul Map documents';
  }
}

/**
 * Editions that hold the Full Soul Map PDF, in order of preference: `full` on
 * the docs-based pipeline (LE-296), `personal` on the LE-241 pipeline.
 */
const FULL_SOUL_MAP_EDITIONS = ['full', 'personal'] as const;

/** Server and client timestamps can disagree by a few seconds. */
const CLOCK_TOLERANCE_MS = 5_000;

/** A typed confirmation's reply is saved seconds after the report records it. */
const CONFIRMATION_REPLY_WINDOW_MS = 2 * 60_000;

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function toTime(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

function findLastIndex<T>(items: T[], predicate: (item: T) => boolean) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) {
      return index;
    }
  }
  return -1;
}

/** The ready Full Soul Map PDF, or null when there is nothing to download. */
export function getFullSoulMapDocument(
  status: SoulMapDocumentStatus | null | undefined
): FullSoulMapDocument | null {
  if (status?.status !== 'ready') {
    return null;
  }

  for (const edition of FULL_SOUL_MAP_EDITIONS) {
    const pdf = status.documents?.[edition]?.pdf;
    const url = pdf?.url ?? status.downloads?.[edition];
    if (url) {
      return {
        url,
        downloadCount: pdf?.download_count ?? 0,
        sizeBytes: pdf?.size_bytes ?? null,
        pageCount: pdf?.page_count ?? null,
      };
    }
  }

  return null;
}

/**
 * The document's phase, or null when there is no document job to show:
 * before confirmation, while the backend only checks completion, after a
 * failure or expiry, or when a ready report has no PDF.
 */
export function getSoulMapDocumentPhase(
  status: SoulMapDocumentStatus | null | undefined,
  options?: SoulMapDocumentPhaseOptions
): SoulMapDocumentPhase | null {
  if (!status) {
    return null;
  }
  if (
    options?.treatExpiredAsPreparing &&
    (status.status === 'ready' || status.status === 'expired')
  ) {
    return 'recomputing';
  }
  if (status.status === 'ready') {
    return getFullSoulMapDocument(status) ? 'ready' : null;
  }
  if (status.status === 'failed' && hasBirthProfileRecomputeError(status)) {
    return 'needs-review';
  }
  if (options?.regenerateExpired && status.status === 'expired') {
    return 'offered';
  }
  return JOB_PHASES[status.status] ?? null;
}

/**
 * Ready with a downloadable PDF, preparing while the job runs, else hidden.
 * The pill stays hidden before confirmation, so an offer shows only in chat.
 */
export function getSoulMapHeaderState(
  status: SoulMapDocumentStatus | null | undefined,
  options?: SoulMapDocumentPhaseOptions
): SoulMapHeaderState {
  const phase = getSoulMapDocumentPhase(status, options);
  if (phase === null || phase === 'offered') return 'hidden';
  if (phase === 'ready') return 'ready';
  if (phase === 'failed') return 'failed';
  if (phase === 'needs-review') return 'needs-review';
  return 'preparing';
}

/**
 * The assistant message the in-chat document card attaches to. The card is
 * rendered from report state rather than stored as a post, so it anchors to
 * the reply that led to the document:
 * 1. the reply saved just after the confirmation was recorded (LE-296), or,
 *    while the document is only offered, just after the offer was recorded,
 * 2. otherwise the last reply before the confirmation, offer or completion,
 * 3. otherwise the latest reply.
 */
export function findSoulMapDocumentAnchorId(
  messages: Message[],
  status: SoulMapDocumentStatus | null | undefined
): string | null {
  const replies = messages
    .filter((message) => !message.isUser && message.text.trim().length > 0)
    .map((message) => ({ id: message.id, time: toTime(message.timestamp) }));

  if (replies.length === 0) {
    return null;
  }

  // Before confirmation the card sits under the agent's offer, which the
  // backend records on the practice when that reply is saved.
  const anchorTime =
    status?.status === 'offered'
      ? toTime(status.offered_at)
      : toTime(status?.confirmed_at);
  if (anchorTime !== null) {
    const anchorReply = replies.find(
      ({ time }) =>
        time !== null &&
        time >= anchorTime - CLOCK_TOLERANCE_MS &&
        time <= anchorTime + CONFIRMATION_REPLY_WINDOW_MS
    );
    if (anchorReply) {
      return anchorReply.id;
    }
  }

  const cutoff = anchorTime ?? toTime(status?.completed_at);
  if (cutoff !== null) {
    const lastBefore = findLastIndex(
      replies,
      ({ time }) => time !== null && time <= cutoff + CLOCK_TOLERANCE_MS
    );
    if (lastBefore >= 0) {
      return replies[lastBefore].id;
    }
  }

  return replies[replies.length - 1].id;
}

/** "11 September 2026" (long) or "11 Sep 2026" (short). */
export function formatSoulMapDocumentDate(
  value: string | null | undefined,
  monthStyle: 'long' | 'short'
): string | null {
  const time = toTime(value);
  if (time === null) {
    return null;
  }
  const date = new Date(time);
  const months = monthStyle === 'long' ? MONTHS_LONG : MONTHS_SHORT;
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function formatFileSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  if (megabytes >= 1) {
    return `${megabytes.toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** "PDF · 14 pages · 1.2 MB", omitting details the API does not provide. */
export function describeSoulMapDocumentFile(pdf: FullSoulMapDocument): string {
  const parts = ['PDF'];
  if (pdf.pageCount && pdf.pageCount > 0) {
    parts.push(`${pdf.pageCount} ${pdf.pageCount === 1 ? 'page' : 'pages'}`);
  }
  if (pdf.sizeBytes && pdf.sizeBytes > 0) {
    parts.push(formatFileSize(pdf.sizeBytes));
  }
  return parts.join(' · ');
}
